import React, { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useDispatch } from "react-redux";


//Layouts
import NonAuthLayout from "../Layouts/NonAuthLayout";
import LandingNavbar from "../pages/Landing/ForecastNestHome/navbar";
import LandingFooter from "../pages/Landing/ForecastNestHome/footer";
import VerticalLayout from "../Layouts/index";

//routes
import { authProtectedRoutes, publicRoutes, openRoutes, isDemoRoute } from "./allRoutes";
import { AuthProtected, AdminOnly } from './AuthProtected';

// Import landing page component
import ForecastNestHome from '../pages/Landing/ForecastNestHome';

// Import login action and api helper
import { loginSuccess } from '../slices/auth/login/reducer';
import { setAuthorization } from '../helpers/api_helper';
import { useProfile } from '../Components/Hooks/UserHooks';

/**
 * Full app chrome when there is a session, the plain shell when there is not.
 *
 * Deliberately not AuthProtected: this is a layout choice, not a gate. A page
 * rendered through here is readable by anyone, and whether a particular forecast
 * may be read is decided by the API.
 */
const OpenLayout = ({ children }) => {
  const { userProfile, token } = useProfile();
  if (userProfile && token) {
    return <VerticalLayout>{children}</VerticalLayout>;
  }
  // The same header and footer the marketing pages use.
  //
  // NonAuthLayout on its own renders nothing around the page, which is right
  // for the login screen and left a visitor here with no way back to the site
  // and no way to sign in. A cut-down bar fixed that much, but it also meant
  // three different chromes across pages open to the public -- and the footer
  // it lacked is the only place Terms, Privacy and the Refund Policy are
  // linked. A visitor arriving on a shared forecast could not reach any of
  // them.
  //
  // The section links in that navbar resolve to "/#section" off the landing
  // page, so they work from here rather than pointing at ids that do not
  // exist.
  //
  // No spacing of its own. The navbar is fixed-top, so something has to clear
  // it, but the pages routed here already do: .page-content carries 69px of
  // padding plus a 70px margin, and the forecast page opens on mt-5 pt-4.
  // Adding the marketing pages' usual "pt-5 mt-5" on top of that put 235px of
  // white above the first heading.
  return (
    <NonAuthLayout>
      <div className="layout-wrapper landing">
        <LandingNavbar />
        {children}
        <LandingFooter />
      </div>
    </NonAuthLayout>
  );
};

// Root route component - redirects "/" based on authentication status
const RootRedirect = () => {
  const { userProfile, token } = useProfile();
  const { hash } = useLocation();

  // If user is authenticated, redirect to /index (dashboard)
  //
  // Unless they asked for a section of the landing page. The policy pages and
  // /how-scoring-works render the marketing navbar whoever is looking, so a
  // signed-in reader clicking "FAQ" there asks for "/#faqs" — and bouncing that
  // to the dashboard answers a different question from the one they asked.
  // A bare "/" still goes to the dashboard, which is what signing in is for.
  if (userProfile && token && !hash) {
    return <Navigate to="/index" replace />;
  }

  // Otherwise, show the landing page
  return (
    <NonAuthLayout>
      <ForecastNestHome />
    </NonAuthLayout>
  );
};

const Index = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const token = urlParams.get('token');
        const userStr = urlParams.get('user');

        if (token && userStr) {
            try {
                const user = JSON.parse(decodeURIComponent(userStr));
                const authUser = { ...user, token };

                // 1. Save to local storage
                localStorage.setItem("authUser", JSON.stringify(authUser));

                // 2. Set authorization token for API calls
                setAuthorization(token);

                // 3. Dispatch login success to update redux state
                dispatch(loginSuccess(authUser));
                
                // 4. Clean the URL and navigate to dashboard
                navigate("/dashboard");

            } catch (error) {
                console.error("Failed to parse user data from URL", error);
                navigate("/login");
            }
        }
    }, [location, dispatch, navigate]);

    return (
        <React.Fragment>
            <Routes>
                {/* Root path - redirects to /index if logged in, otherwise shows landing page */}
                <Route path="/" element={<RootRedirect />} />

                <Route>
                    {publicRoutes.map((route, idx) => (
                        <Route
                            path={route.path}
                            element={
                                // The template's demo pages are registered but
                                // administrators only — twenty of these are fake
                                // sign-in and password-reset screens that needed
                                // no session at all.
                                isDemoRoute(route.path) ? (
                                    <AdminOnly>
                                        <NonAuthLayout>{route.component}</NonAuthLayout>
                                    </AdminOnly>
                                ) : (
                                    <NonAuthLayout>{route.component}</NonAuthLayout>
                                )
                            }
                            key={idx}
                            exact={true}
                        />
                    ))}
                </Route>

                {/*
                    Open to everyone, but shown inside the app for anyone signed
                    in. A shared forecast is a link a member sends to somebody
                    without an account and also one they follow themselves; only
                    the layout differs. What may be read is the API's decision,
                    not this one -- it already refuses a private forecast and a
                    system forecast without an entitlement.
                */}
                <Route>
                    {openRoutes.map((route, idx) => (
                        <Route
                            path={route.path}
                            element={<OpenLayout>{route.component}</OpenLayout>}
                            key={`open-${idx}`}
                            exact={true}
                        />
                    ))}
                </Route>

                <Route>
                    {authProtectedRoutes.map((route, idx) => (
                        <Route
                            path={route.path}
                            element={
                                <AuthProtected>
                                    {isDemoRoute(route.path) ? (
                                        <AdminOnly>
                                            <VerticalLayout>{route.component}</VerticalLayout>
                                        </AdminOnly>
                                    ) : (
                                        <VerticalLayout>{route.component}</VerticalLayout>
                                    )}
                                </AuthProtected>}
                            key={idx}
                            exact={true}
                        />
                    ))}
                </Route>
            </Routes>
        </React.Fragment>
    );
};

export default Index;
